import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Layout } from './components/Layout'
import { ActionsProvider } from './lib/actions'
import { useEffect, useState } from 'react'
import { Login } from './pages/Login'
import { PortalApp } from './portal/PortalApp'
import { getToken, tokenVerlooptOp, vernieuwSessie } from './lib/api'
import { NAV } from './nav'
import { Foutgrens } from './components/Foutgrens'
import { eersteToegestaneRoute, isManager, magRoute, useMij } from './lib/rechten'
import { Loading } from './components/ui'
import Dashboard from './pages/Dashboard'
import Models from './pages/Models'
import Posting from './pages/Posting'
import PaidPosting from './pages/PaidPosting'
import Instagram from './pages/Instagram'
import MassMessages from './pages/MassMessages'
import Chatters from './pages/Chatters'
import Chats from './pages/Chats'
import Fans from './pages/Fans'
import Content from './pages/Content'
import Earnings from './pages/Earnings'
import Analytics from './pages/Analytics'
import FollowerTracking from './pages/FollowerTracking'
import Tracking from './pages/Tracking'
import PromoMM from './pages/PromoMM'
import ModelTeksten from './pages/ModelTeksten'
import Hashtags from './pages/Hashtags'
import Rules from './pages/Rules'
import Logs from './pages/Logs'
import Settings from './pages/Settings'
import HelpLog from './pages/HelpLog'
import { Placeholder } from './pages/Placeholder'
import ChatWindow from './pages/ChatWindow'
import ModelShow from './pages/ModelShow'
import FanShow from './pages/FanShow'
import ModelEdit from './pages/ModelEdit'
import RuleEdit from './pages/RuleEdit'
import PostCreate from './pages/PostCreate'
import F2fInloggen from './pages/F2fInloggen'

/** Pagina's die al gebouwd zijn. De rest valt op een expliciete
 *  "nog niet gebouwd"-pagina, zodat nooit onduidelijk is wat af is. */
const PAGES: Record<string, () => JSX.Element> = {
  dashboard: Dashboard,
  personas: Models,
  posting: Posting,
  'paid-posting': PaidPosting,
  // 24-10-2026 (Jordy): Instagram fase 1 (dry-run).
  instagram: Instagram,
  'mass-messages': MassMessages,
  chatters: Chatters,
  conversations: Chats,
  fans: Fans,
  content: Content,
  earnings: Earnings,
  analytics: Analytics,
  'follower-tracking': FollowerTracking,
  tracking: Tracking,
  'promo-mm': PromoMM,
  'model-texts': ModelTeksten,
  hashtags: Hashtags,
  rules: Rules,
  console: Logs,
  settings: Settings,
  'help-log': HelpLog,
  'f2f-login': F2fInloggen,
}

/**
 * De poortwachter voor één route.
 *
 * Verbergen in de zijbalk is niet genoeg: een chatter die /settings intypt komt
 * er anders gewoon. Dit is de tweede plek in de frontend; de derde en
 * beslissende is de backend, die 403 geeft.
 */
function Bewaakt({ route, children }: { route: string; children: JSX.Element }) {
  const mij = useMij()
  if (mij.loading) return <Loading />
  if (!magRoute(mij.data, route)) {
    // 05-10-2026 (Jordy): een manager gaat naar een pagina die hij wel mag,
    // in plaats van een "No access"-kaart.
    // 18-10-2026 (Jordy): een chatter kwam via / op /dashboard binnen (alleen
    // beheer) en zag eerst "No access". Hij gaat nu naar Chats, of anders naar
    // de eerste pagina die hij wel mag.
    const chatter = String(mij.data?.role ?? '').toLowerCase() === 'chatter'
    const naar = isManager(mij.data)
      ? eersteToegestaneRoute(mij.data, NAV.map((n) => n.route))
      : chatter
        ? eersteToegestaneRoute(mij.data, ['conversations', ...NAV.map((n) => n.route)])
        : null
    if (naar) return <Navigate to={`/${naar}`} replace />
    return (
      <div className="card" style={{ maxWidth: 520 }}>
        <h3 style={{ marginTop: 0 }}>No access</h3>
        <p className="mut2" style={{ fontSize: 13 }}>
          This page is not part of your role. Ask an administrator for
          access if you need it.
        </p>
      </div>
    )
  }
  // Elke pagina achter een foutgrens. Valt er één om, dan blijft de rest van
  // het dashboard staan in plaats van dat het scherm zwart wordt.
  return <Foutgrens waar={route}>{children}</Foutgrens>
}

export function App() {
  // 02-10-2026 (fase 2): het model-portal staat helemaal los van het dashboard
  // — eigen token, geen menu, geen admin-inlog. Zie src/portal/.
  if (window.location.pathname.startsWith('/portal')) return <PortalApp />
  return <BeheerApp />
}

function BeheerApp() {
  // Zonder geldig token het inlogscherm, zoals de bundle. Verloopt de sessie
  // (401 uit lib/api), dan wist die het token en komt dit scherm terug.
  const [authed, setAuthed] = useState(() => !!getToken())
  // 03-10-2026: zonder token eerst stil proberen met de refresh-cookie
  // ("Remember me"); pas als dat niet lukt het inlogscherm.
  const [proberen, setProberen] = useState(() => !getToken())
  useEffect(() => {
    if (!proberen) return
    void vernieuwSessie().then((ok) => { if (ok) setAuthed(true); setProberen(false) })
  }, [proberen])
  useEffect(() => {
    const t = window.setInterval(() => setAuthed(!!getToken()), 4000)
    // Vernieuwen vóór het token verloopt, zodat er geen 401-golf komt.
    const v = window.setInterval(() => {
      const op = tokenVerlooptOp()
      if (op != null && op - Date.now() < 10 * 60_000) void vernieuwSessie()
    }, 60_000)
    return () => { window.clearInterval(t); window.clearInterval(v) }
  }, [])
  if (proberen) return null
  if (!authed) return <Login onLoggedIn={() => setAuthed(true)} />
  return (
    <BrowserRouter>
      <ActionsProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {NAV.map((n) => {
            const Page = PAGES[n.route]
            return (
              <Route
                key={n.route}
                path={`/${n.route}`}
                element={
                  <Bewaakt route={n.route}>
                    {Page ? <Page /> : <Placeholder name={n.label} />}
                  </Bewaakt>
                }
              />
            )
          })}
          {/* Het chatvenster waar chatters in werken. Zonder dit scherm is
              het dashboard voor een chatter niet bruikbaar. */}
          <Route path="/conversations/show/:id" element={<Bewaakt route="conversations"><ChatWindow /></Bewaakt>} />
          {/* Subroutes die in oud ECHT bestaan — gemeten door ze te openen,
              zie /root/inventaris-v2/routes.md. */}
          <Route path="/personas/show/:id" element={<Bewaakt route="personas"><ModelShow /></Bewaakt>} />
          <Route path="/personas/edit/:id" element={<Bewaakt route="personas"><ModelEdit /></Bewaakt>} />
          <Route path="/fans/show/:id" element={<Bewaakt route="fans"><FanShow /></Bewaakt>} />
          <Route path="/rules/create" element={<Bewaakt route="rules"><RuleEdit /></Bewaakt>} />
          <Route path="/rules/edit/:id" element={<Bewaakt route="rules"><RuleEdit /></Bewaakt>} />

          {/* "+ Create Manual Post" wijst hierheen, net als in de oude bundle. */}
          <Route path="/posting/create" element={<Bewaakt route="posting"><PostCreate /></Bewaakt>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>
      </ActionsProvider>
    </BrowserRouter>
  )
}
